/* ==========================================================================
   TTT audio PDP — 1.8 redesign behaviour
   Built 2026-08-15 against docs/2026-08-15-design-pdp-redesign-spec.md.

   Four jobs, and nothing else:
     1. lazy video embeds (unchanged behaviour, moved here)
     2. the mobile comparison pill group — a REAL radio group (spec §6)
     3. the section nav's current-section state
     4. the mobile buy bar, synced to the Dawn variant including sold-out

   Deliberately not here: no colour sampling on the hero (spec §4.1.3), no
   height animation on the comparison switch (spec §6), no rebuilt radio
   semantics — the browser's own radio group gives roving tabindex, arrow
   keys, Home/End and the checked state for free, and re-implementing it with
   divs is how keyboard passes get lost.
   ========================================================================== */

window.tttPdpEmbedUrl =
  window.tttPdpEmbedUrl ||
  function tttPdpEmbedUrl(rawUrl) {
    try {
      const url = new URL(rawUrl, window.location.href);
      const host = url.hostname.replace(/^www\./, '');
      let videoId = '';

      if (host === 'youtube.com' || host === 'm.youtube.com') {
        videoId =
          url.pathname === '/watch'
            ? url.searchParams.get('v') || ''
            : url.pathname.match(/^\/(?:embed|shorts)\/([^/]+)/)?.[1] || '';
      } else if (host === 'youtu.be') {
        videoId = url.pathname.split('/').filter(Boolean)[0] || '';
      }

      if (videoId && /^[A-Za-z0-9_-]+$/.test(videoId)) {
        return `https://www.youtube-nocookie.com/embed/${videoId}?playsinline=1`;
      }
    } catch (_error) {
      /* a verified non-YouTube embed URL is used unchanged */
    }
    return rawUrl;
  };

function tttLoadEmbed(event) {
  const button = event.currentTarget;
  const slot = button.closest('[data-ttt-media-slot]');
  const src = button.dataset.tttLazyEmbed;
  if (!slot || !src) return;

  const iframe = document.createElement('iframe');
  iframe.src = window.tttPdpEmbedUrl(src);
  iframe.title = button.dataset.tttEmbedTitle || 'Video';
  iframe.loading = 'lazy';
  iframe.allow =
    'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
  iframe.allowFullscreen = true;
  slot.replaceChildren(iframe);
}

/* -------------------------------------------------------------------------
   Journey host
   ------------------------------------------------------------------------- */

if (!customElements.get('ttt-product-journey')) {
  customElements.define(
    'ttt-product-journey',
    class TttProductJourney extends HTMLElement {
      connectedCallback() {
        this.querySelectorAll('[data-ttt-lazy-embed]').forEach((button) => {
          button.addEventListener('click', tttLoadEmbed, { once: true });
        });

        this.initComparison();
        this.initNav();
      }

      disconnectedCallback() {
        this.navObserver?.disconnect();
      }

      /* --- comparison pill group ------------------------------------------
         Changing the selection re-renders the difference rows only. It does
         not scroll, does not move focus and does not animate height. */
      initComparison() {
        this.pillInputs = Array.from(this.querySelectorAll('[data-ttt-compare-pill]'));
        if (!this.pillInputs.length) return;

        this.pillInputs.forEach((input) => {
          input.addEventListener('change', () => this.renderComparison(input.value));
        });

        const checked = this.pillInputs.find((i) => i.checked) || this.pillInputs[0];
        if (checked) {
          checked.checked = true;
          this.renderComparison(checked.value);
        }
      }

      renderComparison(index) {
        this.querySelectorAll('[data-ttt-compare-panel]').forEach((panel) => {
          panel.hidden = panel.dataset.tttComparePanel !== String(index);
        });
      }

      /* --- section nav ---------------------------------------------------- */
      initNav() {
        const nav = document.querySelector('[data-ttt-pdp-nav]');
        if (!nav) return;

        const links = Array.from(nav.querySelectorAll('a[href^="#"]'));
        const targets = links
          .map((link) => document.getElementById(link.getAttribute('href').slice(1)))
          .filter(Boolean);
        if (!targets.length) return;

        this.navObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              links.forEach((link) => {
                const on = link.getAttribute('href') === `#${entry.target.id}`;
                if (on) {
                  link.setAttribute('aria-current', 'true');
                } else {
                  link.removeAttribute('aria-current');
                }
              });
            });
          },
          { rootMargin: '-52px 0px -70% 0px', threshold: 0 }
        );

        targets.forEach((t) => this.navObserver.observe(t));
      }
    }
  );
}

/* -------------------------------------------------------------------------
   Evidence host
   ------------------------------------------------------------------------- */

if (!customElements.get('ttt-product-evidence')) {
  customElements.define(
    'ttt-product-evidence',
    class TttProductEvidence extends HTMLElement {
      connectedCallback() {
        this.querySelectorAll('[data-ttt-lazy-embed]').forEach((button) => {
          button.addEventListener('click', tttLoadEmbed, { once: true });
        });
      }
    }
  );
}

/* -------------------------------------------------------------------------
   CTA price

   The concept puts the label left and the price right inside the add-to-cart
   button (spec §4.2 #10). Dawn's button renders one span, and forking
   main-product.liquid for a second one is not worth it, so the price span is
   appended here and re-read from the rendered price element on every variant
   change. It never computes a price itself — if the buy block and the button
   ever disagree, the button is the one that is wrong, and reading rather than
   calculating is what stops that happening.
   ------------------------------------------------------------------------- */

/* ⚠ THE PRICE SPAN LIVES INSIDE THE OBSERVED SUBTREE.

   The observer below watches `product-info` with `subtree: true`, and this
   function writes into a button inside `product-info`. Writing unconditionally
   is therefore a self-feeding loop: the write queues a childList record, the
   record re-runs this function, the function writes again. MutationObserver
   callbacks are microtasks, so that loop never yields to the event loop and the
   tab freezes with the page half-painted. It shipped that way on 2026-08-15 and
   took the five redesigned PDPs down.

   Two independent guards, because one is not worth trusting on a live PDP:

     1. Every mutation is wrapped in `tttMutateUnobserved`, which disconnects the
        observer, mutates, drops the records the mutation queued, and reconnects.
     2. Nothing is written unless the value actually changed. `textContent = x`
        replaces the text node even when `x` is identical, so an equality check
        is what stops a no-op write from queueing a record at all.

   Keep both if you touch this. */

let tttCtaObserver = null;
let tttCtaObserved = null;

function tttMutateUnobserved(mutate) {
  if (!tttCtaObserver || !tttCtaObserved) {
    mutate();
    return;
  }
  tttCtaObserver.disconnect();
  try {
    mutate();
  } finally {
    tttCtaObserver.takeRecords();
    tttCtaObserver.observe(tttCtaObserved, { childList: true, subtree: true });
  }
}

function tttSyncCtaPrice() {
  const info = document.querySelector('product-info');
  if (!info) return;
  const submit = info.querySelector('product-form button[name="add"]');
  const price = info.querySelector(
    '[id^="price-"] .price-item--sale, [id^="price-"] .price-item--last, [id^="price-"] .price-item--regular'
  );
  if (!submit || !price) return;

  const existing = submit.querySelector('.ttt-cta__price');

  if (submit.disabled || submit.getAttribute('aria-disabled') === 'true') {
    if (existing) tttMutateUnobserved(() => existing.remove());
    return;
  }

  const text = price.textContent.trim();

  if (existing) {
    if (existing.textContent !== text) {
      tttMutateUnobserved(() => {
        existing.textContent = text;
      });
    }
    return;
  }

  tttMutateUnobserved(() => {
    const span = document.createElement('span');
    span.className = 'ttt-cta__price';
    span.textContent = text;
    submit.appendChild(span);
  });
}

document.addEventListener('DOMContentLoaded', () => {
  if (!document.body.classList.contains('ttt-pdp-page')) return;
  /* Three sections each request this file, so the browser runs it more than
     once per page. Without this flag every copy installs its own observer and
     its own subscription on the same node. */
  if (window.tttPdpCtaBound) return;
  window.tttPdpCtaBound = true;

  const info = document.querySelector('product-info');
  if (info) {
    tttCtaObserved = info;
    tttCtaObserver = new MutationObserver(() => tttSyncCtaPrice());
    tttCtaObserver.observe(info, { childList: true, subtree: true });
  }

  tttSyncCtaPrice();

  if (window.PUB_SUB_EVENTS?.variantChange && typeof subscribe === 'function') {
    subscribe(PUB_SUB_EVENTS.variantChange, () => window.requestAnimationFrame(tttSyncCtaPrice));
  }
});

/* -------------------------------------------------------------------------
   Mobile buy bar

   It never holds its own idea of price, availability or label. Everything is
   read back out of the rendered buy block after each variant change, because
   that block is the thing the customer and the cart both agree with. The 1.5
   sold-out swap and the notify-me form are therefore inherited rather than
   duplicated.
   ------------------------------------------------------------------------- */

if (!customElements.get('ttt-buy-bar')) {
  customElements.define(
    'ttt-buy-bar',
    class TttBuyBar extends HTMLElement {
      connectedCallback() {
        this.button = this.querySelector('[data-ttt-buybar-button]');
        this.priceEl = this.querySelector('[data-ttt-buybar-price]');
        this.variantEl = this.querySelector('[data-ttt-buybar-variant]');
        this.button?.addEventListener('click', this.onClick.bind(this));

        this.sync();

        if (window.PUB_SUB_EVENTS?.variantChange && typeof subscribe === 'function') {
          this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, () => {
            window.requestAnimationFrame(() => this.sync());
          });
        }

        this.productInfo = document.querySelector('product-info');
        if (this.productInfo) {
          this.observer = new MutationObserver(() => this.sync());
          this.observer.observe(this.productInfo, { childList: true, subtree: true });
        }
      }

      disconnectedCallback() {
        this.unsubscribe?.();
        this.observer?.disconnect();
      }

      parts() {
        const info = document.querySelector('product-info');
        return {
          submit: info?.querySelector('product-form button[name="add"]'),
          notify: info?.querySelector('[data-ttt-notify]'),
          discontinued: info?.querySelector('.ttt-discontinued'),
          price: info?.querySelector(
            '[id^="price-"] .price-item--sale, [id^="price-"] .price-item--last, [id^="price-"] .price-item--regular'
          ),
          variantName: info?.querySelector(
            'variant-selects input[type="radio"]:checked, .product-form__input input[type="radio"]:checked'
          ),
        };
      }

      sync() {
        if (!this.button) return;
        const { submit, notify, discontinued, price, variantName } = this.parts();

        if (this.priceEl && price) this.priceEl.textContent = price.textContent.trim();
        if (this.variantEl) {
          const label = variantName?.value || variantName?.getAttribute('value') || '';
          this.variantEl.textContent = label;
          this.variantEl.hidden = !label;
        }

        if (submit && !submit.disabled && submit.getAttribute('aria-disabled') !== 'true') {
          this.button.textContent = submit.querySelector('span')?.textContent?.trim() || 'Add to bag';
          this.button.disabled = false;
          this.mode = 'submit';
          this.hidden = false;
          return;
        }

        if (notify) {
          this.button.textContent = 'Notify me';
          this.button.disabled = false;
          this.mode = 'scroll';
          this.hidden = false;
          return;
        }

        if (discontinued) {
          this.button.textContent = 'No longer sold';
          this.button.disabled = true;
          this.mode = 'disabled';
          this.hidden = false;
          return;
        }

        this.button.textContent = submit?.querySelector('span')?.textContent?.trim() || 'Sold out';
        this.button.disabled = true;
        this.mode = 'disabled';
        this.hidden = false;
      }

      onClick() {
        const { submit, notify } = this.parts();
        if (this.mode === 'submit' && submit && !submit.disabled) {
          submit.click();
          return;
        }
        const target = notify || document.querySelector('product-info [id^="TttBuyAction-"]');
        target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        target?.querySelector('input, button')?.focus({ preventScroll: true });
      }
    }
  );
}
